'use client';

import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { LYRRA_A11Y_STORAGE_KEY, LYRRA_A11Y_UPDATED_EVENT } from '@/hooks/useKeyboardNavigationSetting';

const ZOOM = 1.85;
const RADIUS_PX = 100;
const SIZE = RADIUS_PX * 2;
const POINTER_SMOOTH = 0.16;

export type PlayerMagnifierCloneMode = 'firstInner' | 'root';

export type PlayerMagnifierRegion = {
  id: string;
  getBounds: () => HTMLElement | null;
  /** Pour firstInner : le conteneur dont on clone le premier enfant (ex. zone scroll). Pour root : le même que la cible de clone. */
  getCloneRoot: () => HTMLElement | null;
  cloneMode: PlayerMagnifierCloneMode;
};

export type PlayerMagnifierRegionsBundle = {
  regions: PlayerMagnifierRegion[];
  /** Ex. survol du voile du sommaire mobile sans être sur le panneau : pas de loupe sur le contenu derrière. */
  suppressAt?: (clientX: number, clientY: number) => boolean;
};

function readMouseMagnifier(): boolean {
  try {
    const raw = localStorage.getItem(LYRRA_A11Y_STORAGE_KEY);
    if (!raw) return false;
    const o = JSON.parse(raw) as { mouseMagnifier?: boolean };
    return Boolean(o.mouseMagnifier);
  } catch {
    return false;
  }
}

/** Attribut posé sur la racine de la loupe : utilisé pour la repérer dans le DOM
 * et la retirer du clone (sinon la loupe s'auto-clone à l'infini → effet de
 * dédoublement visible à l'écran). */
const MAGNIFIER_OVERLAY_ATTR = 'data-lyrra-magnifier-overlay';

function stripIdsAndDisableInteraction(root: HTMLElement) {
  root.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
  root.setAttribute('aria-hidden', 'true');
  root.style.pointerEvents = 'none';
  root.style.userSelect = 'none';
}

/**
 * Supprime les overlays de la loupe (curseur fake, lentille, calque) du clone.
 * Sans ça, on cloneNode `body` qui contient déjà notre loupe via createPortal :
 * la loupe contient sa propre image → on voit le contenu réel + un fantôme
 * de la loupe agrandi à l'intérieur du cercle.
 */
function removeMagnifierFromClone(cloneRoot: HTMLElement): void {
  if (cloneRoot.matches?.(`[${MAGNIFIER_OVERLAY_ATTR}]`)) {
    cloneRoot.remove();
    return;
  }
  cloneRoot
    .querySelectorAll(`[${MAGNIFIER_OVERLAY_ATTR}]`)
    .forEach((el) => el.remove());
}

/**
 * Force la dimension du clone au match du live element.
 *
 * Le clone est extrait de son parent (souvent un conteneur flex) et inséré
 * dans un wrapper neutre : les classes comme `flex-1`, `w-full`, etc. n'ont
 * plus de référence et l'élément collapse sur son contenu intrinsèque → zoom
 * sur le vide. On reproduit les dimensions effectives du live element.
 */
function fixCloneDimensions(liveRoot: HTMLElement, cloneRoot: HTMLElement): void {
  const width = Math.max(liveRoot.offsetWidth, liveRoot.scrollWidth);
  const height = Math.max(liveRoot.offsetHeight, liveRoot.scrollHeight);
  if (width > 0) cloneRoot.style.setProperty('width', `${width}px`, 'important');
  if (height > 0) cloneRoot.style.setProperty('height', `${height}px`, 'important');
  // Bloque tout calque transformé qui forcerait la transparence (ex. dialog
  // ouverte à scale 0 dans une animation framer-motion en cours).
  cloneRoot.style.removeProperty('transform');
  cloneRoot.style.removeProperty('opacity');
  cloneRoot.style.removeProperty('visibility');
  cloneRoot.style.removeProperty('animation');
}

/**
 * Copie l'état d'interaction des éléments vivants (scrollTop/scrollLeft,
 * valeur des champs, état des checkbox/radio, canvas) sur les nœuds clonés.
 *
 * Sans cette synchronisation, un `<main overflow-y-auto>` cloné aurait
 * `scrollTop=0`, et la loupe afficherait le haut du document au lieu de la
 * zone effectivement visible sous le curseur.
 */
function syncLiveStateIntoClone(
  liveRoot: HTMLElement,
  cloneRoot: HTMLElement,
): void {
  const liveScrollable: HTMLElement[] = [];
  const liveAll = liveRoot.querySelectorAll<HTMLElement>('*');
  liveScrollable.push(liveRoot);
  liveAll.forEach((el) => liveScrollable.push(el));

  const cloneAll = cloneRoot.querySelectorAll<HTMLElement>('*');
  const cloneList: HTMLElement[] = [cloneRoot];
  cloneAll.forEach((el) => cloneList.push(el));

  if (liveScrollable.length !== cloneList.length) {
    // Les deux arbres ont divergé (rarement, ex. portails). On tente quand même
    // de synchroniser les premiers nœuds appariés.
  }

  const max = Math.min(liveScrollable.length, cloneList.length);
  for (let i = 0; i < max; i++) {
    const live = liveScrollable[i];
    const clone = cloneList[i];
    if (!live || !clone) continue;

    clone.scrollTop = live.scrollTop;
    clone.scrollLeft = live.scrollLeft;

    if (live instanceof HTMLInputElement && clone instanceof HTMLInputElement) {
      if (live.type === 'checkbox' || live.type === 'radio') {
        clone.checked = live.checked;
      } else {
        clone.value = live.value;
      }
    } else if (
      live instanceof HTMLTextAreaElement &&
      clone instanceof HTMLTextAreaElement
    ) {
      clone.value = live.value;
    } else if (live instanceof HTMLSelectElement && clone instanceof HTMLSelectElement) {
      clone.value = live.value;
    }
  }
}

/** Agrandissement sur ce nœud uniquement (le calque parent ne fait que translater ; ne pas mélanger zoom CSS + scale). */
function applyMagnifyToNode(el: HTMLElement, zoom: number) {
  el.style.transformOrigin = '0 0';
  const t = `scale(${zoom})`;
  el.style.setProperty('transform', t);
  el.style.setProperty('-webkit-transform', t);
}

type FocusPayload = {
  relX: number;
  relY: number;
  innerWidth: number;
  cloneSourceEl: HTMLElement;
  cloneMode: PlayerMagnifierCloneMode;
  regionId: string;
};

function getCloneSubtree(mount: HTMLDivElement): HTMLElement | null {
  const zoomWrap = mount.firstElementChild as HTMLElement | null;
  return (zoomWrap?.firstElementChild as HTMLElement | null) ?? null;
}

/** Resynchronise scroll / champs sur le clone existant sans le reconstruire. */
function syncExistingCloneMount(mount: HTMLDivElement, focus: FocusPayload): void {
  const subtree = getCloneSubtree(mount);
  if (!subtree) return;

  if (focus.cloneMode === 'firstInner') {
    const first = focus.cloneSourceEl.firstElementChild as HTMLElement | null;
    if (first) syncLiveStateIntoClone(first, subtree);
  } else {
    syncLiveStateIntoClone(focus.cloneSourceEl, subtree);
  }
}

function rebuildCloneMount(mount: HTMLDivElement, focus: FocusPayload | null) {
  mount.innerHTML = '';
  if (!focus) return;

  let subtree: HTMLElement | null = null;
  let liveSource: HTMLElement | null = null;

  if (focus.cloneMode === 'firstInner') {
    const first = focus.cloneSourceEl.firstElementChild as HTMLElement | null;
    if (first) {
      liveSource = first;
      subtree = first.cloneNode(true) as HTMLElement;
      stripIdsAndDisableInteraction(subtree);
    }
  } else {
    liveSource = focus.cloneSourceEl;
    subtree = focus.cloneSourceEl.cloneNode(true) as HTMLElement;
    stripIdsAndDisableInteraction(subtree);
  }

  if (subtree) {
    if (liveSource) {
      fixCloneDimensions(liveSource, subtree);
      syncLiveStateIntoClone(liveSource, subtree);
    }
    removeMagnifierFromClone(subtree);

    const zoomWrap = document.createElement('div');
    zoomWrap.style.willChange = 'transform';
    applyMagnifyToNode(zoomWrap, ZOOM);
    zoomWrap.appendChild(subtree);
    mount.appendChild(zoomWrap);
  }
}

/**
 * Loupe : le clone est injecté en DOM impératif. Ne pas appeler setState à chaque frame :
 * React réconcilierait le conteneur comme vide et supprimerait le clone (loupe vide).
 * Position / transform mis à jour via refs + requestAnimationFrame.
 */
function PlayerContentMagnifierInner({
  regionsBundleRef,
  refreshKey,
}: {
  regionsBundleRef: React.MutableRefObject<PlayerMagnifierRegionsBundle>;
  refreshKey: string;
}) {
  const [enabled, setEnabled] = useState(readMouseMagnifier);
  const [cloneGeneration, setCloneGeneration] = useState(0);

  const lensBoxRef = useRef<HTMLDivElement | null>(null);
  const scaledLayerRef = useRef<HTMLDivElement | null>(null);
  const cloneMountRef = useRef<HTMLDivElement | null>(null);

  const targetRef = useRef({ x: 0, y: 0 });
  const smoothRef = useRef({ x: 0, y: 0 });
  const smoothInitRef = useRef(false);
  const rafRef = useRef<number>(0);
  const lastRebuildTokenRef = useRef<string | null>(null);

  const computeFocus = useCallback((clientX: number, clientY: number): FocusPayload | null => {
    const bundle = regionsBundleRef.current;
    if (bundle.suppressAt?.(clientX, clientY)) return null;

    for (const r of bundle.regions) {
      const boundsEl = r.getBounds();
      if (!boundsEl) continue;

      const rect = boundsEl.getBoundingClientRect();
      if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) {
        continue;
      }

      const root = r.getCloneRoot();
      if (!root) continue;

      if (r.cloneMode === 'firstInner') {
        const first = root.firstElementChild as HTMLElement | null;
        if (!first) continue;
        const fRect = first.getBoundingClientRect();
        return {
          relX: clientX - fRect.left,
          relY: clientY - fRect.top,
          innerWidth: first.offsetWidth,
          cloneSourceEl: root,
          cloneMode: 'firstInner',
          regionId: r.id,
        };
      }

      const fRect = root.getBoundingClientRect();
      return {
        relX: clientX - fRect.left,
        relY: clientY - fRect.top,
        innerWidth: root.offsetWidth,
        cloneSourceEl: root,
        cloneMode: 'root',
        regionId: r.id,
      };
    }
    return null;
  }, [regionsBundleRef]);

  const rebuildToken = useCallback(
    (focus: FocusPayload | null): string => {
      if (!focus) return '__none__';
      return `${focus.regionId}:${refreshKey}`;
    },
    [refreshKey],
  );

  useEffect(() => {
    const sync = () => setEnabled(readMouseMagnifier());
    window.addEventListener(LYRRA_A11Y_UPDATED_EVENT, sync);
    return () => window.removeEventListener(LYRRA_A11Y_UPDATED_EVENT, sync);
  }, []);

  useLayoutEffect(() => {
    if (!enabled) return;

    const mount = cloneMountRef.current;
    if (!mount) return;

    const focus = computeFocus(smoothRef.current.x, smoothRef.current.y);
    rebuildCloneMount(mount, focus);
  }, [enabled, refreshKey, cloneGeneration, computeFocus]);

  useLayoutEffect(() => {
    if (!enabled) return;
    const mount = cloneMountRef.current;
    if (!mount || mount.firstChild) return;
    const focus = computeFocus(smoothRef.current.x, smoothRef.current.y);
    rebuildCloneMount(mount, focus);
  });

  useEffect(() => {
    if (!enabled) {
      smoothInitRef.current = false;
      lastRebuildTokenRef.current = null;
      return;
    }
    lastRebuildTokenRef.current = null;
    smoothRef.current = { x: 0, y: 0 };
    targetRef.current = { x: 0, y: 0 };
    smoothInitRef.current = false;

    const box = lensBoxRef.current;
    const scaled = scaledLayerRef.current;
    if (box) {
      box.style.opacity = '0';
      box.style.visibility = 'hidden';
    }
    if (scaled) {
      scaled.style.setProperty('transform', 'none');
      scaled.style.setProperty('-webkit-transform', 'none');
      scaled.style.removeProperty('zoom');
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;

    const onMove = (e: PointerEvent) => {
      targetRef.current = { x: e.clientX, y: e.clientY };
      if (!smoothInitRef.current) {
        smoothRef.current = { x: e.clientX, y: e.clientY };
        smoothInitRef.current = true;
      }
    };

    window.addEventListener('pointermove', onMove, { passive: true });

    const tick = () => {
      const t = targetRef.current;
      const s = smoothRef.current;
      s.x += (t.x - s.x) * POINTER_SMOOTH;
      s.y += (t.y - s.y) * POINTER_SMOOTH;

      const focus = computeFocus(s.x, s.y);
      const token = rebuildToken(focus);
      if (token !== lastRebuildTokenRef.current) {
        lastRebuildTokenRef.current = token;
        setCloneGeneration((g) => g + 1);
      } else if (focus) {
        const mount = cloneMountRef.current;
        if (mount?.firstChild) syncExistingCloneMount(mount, focus);
      }

      const box = lensBoxRef.current;
      const scaled = scaledLayerRef.current;

      if (box) {
        box.style.width = `${SIZE}px`;
        box.style.height = `${SIZE}px`;
        box.style.left = `${s.x - RADIUS_PX}px`;
        box.style.top = `${s.y - RADIUS_PX}px`;

        if (!focus) {
          box.style.opacity = '0';
          box.style.visibility = 'hidden';
        } else {
          box.style.opacity = '1';
          box.style.visibility = 'visible';
        }
      }

      if (scaled && focus) {
        const { relX, relY, innerWidth } = focus;
        scaled.style.width = `${innerWidth}px`;
        const tx = RADIUS_PX - relX * ZOOM;
        const ty = RADIUS_PX - relY * ZOOM;
        const tr = `translate3d(${tx}px, ${ty}px, 0)`;
        scaled.style.setProperty('transform', tr);
        scaled.style.setProperty('-webkit-transform', tr);
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('pointermove', onMove);
      cancelAnimationFrame(rafRef.current);
    };
  }, [enabled, computeFocus, rebuildToken]);

  if (!enabled) return null;

  return createPortal(
    <div
      className="pointer-events-none"
      aria-hidden
      {...{ [MAGNIFIER_OVERLAY_ATTR]: 'true' }}
    >
      <div
        ref={lensBoxRef}
        {...{ [MAGNIFIER_OVERLAY_ATTR]: 'true' }}
        className="fixed z-[9600] overflow-hidden rounded-full border-[3px] border-amber-500/95 shadow-[0_12px_40px_rgba(0,0,0,0.35)] ring-2 ring-black/15 dark:border-amber-400 dark:ring-white/10"
        style={{
          width: SIZE,
          height: SIZE,
          left: 0,
          top: 0,
          opacity: 0,
          visibility: 'hidden',
          background: 'rgba(255,255,255,0.04)',
        }}
      >
        <div className="absolute inset-0 overflow-hidden rounded-full" style={{ width: SIZE, height: SIZE }}>
          <div ref={scaledLayerRef} className="origin-top-left">
            <div ref={cloneMountRef} />
          </div>
        </div>
        {/* Faux pointeur : le clone DOM ne montre pas le curseur natif.
            On dessine une cible centrée pour que l'utilisateur sache où il pointe. */}
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          width="22"
          height="22"
          style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.55))' }}
        >
          <circle cx="12" cy="12" r="3" fill="none" stroke="#ffffff" strokeWidth="2.5" />
          <circle cx="12" cy="12" r="3" fill="none" stroke="#001B3D" strokeWidth="1" />
          <line x1="12" y1="2" x2="12" y2="7" stroke="#001B3D" strokeWidth="1.5" />
          <line x1="12" y1="17" x2="12" y2="22" stroke="#001B3D" strokeWidth="1.5" />
          <line x1="2" y1="12" x2="7" y2="12" stroke="#001B3D" strokeWidth="1.5" />
          <line x1="17" y1="12" x2="22" y2="12" stroke="#001B3D" strokeWidth="1.5" />
        </svg>
      </div>
    </div>,
    document.body,
  );
}

export const PlayerContentMagnifier = React.memo(PlayerContentMagnifierInner, (a, b) => {
  return a.refreshKey === b.refreshKey && a.regionsBundleRef === b.regionsBundleRef;
});
